import { getEndpoints } from '../Config/EndPoints/EndPoints';
import { decodeData, encodeData, extractPathnameFromUrl } from './general';
import { getToken, isAnonymousSession } from './SessionStorageUtil';
import { TTL } from './TTL';

export interface IApiConfig {
  path: string;
  method: string;
  ttl?: number;
  enableCache?: boolean;
  persist?: boolean;
  showLoading?: boolean;
  consideredHeaders?: string[];
}

interface IApiConfigStorage {
  configList: IApiConfig[];
  timestamp: number;
  loggedIn: boolean;
}

// This is the default API config path and not in use
const DEFAULT_API_CONFIG_PATH = '/apiConfig.json';

class ApiConfigManager {
  private NON_LOGIN_REFRESH_INTERVAL = 24 * 60 * 60 * 1000;
  private API_CONFIG_PATH = "";
  private API_CONFIG_STORAGE_KEY = 'apiConfig';
  private isUserLoggedIn: boolean = false;
  private apiConfigList: IApiConfig[] | null = null;
  private fetchConfigPromise: Promise<IApiConfig[]> | null = null;

  isConfigCached(): boolean {
    try {
      const storedData = localStorage.getItem(this.API_CONFIG_STORAGE_KEY);
      if (!storedData) return false;
      
      const storageObj = decodeData<IApiConfigStorage>(storedData);
      if (!storageObj) return false;
      
      const { timestamp } = storageObj;
      const now = Date.now();
      
      if (!this.isUserLoggedIn) {
        if ((now - timestamp) > this.NON_LOGIN_REFRESH_INTERVAL) {
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Failed to check cached config:', error);
      return false;
    }
  }

  async fetchApiConfig(forceFetch?: boolean): Promise<IApiConfig[]> {
    if (!this.API_CONFIG_PATH) {
      const endpoints = await getEndpoints();
      this.API_CONFIG_PATH = endpoints.getApiConfigAPI
    }

    if (this.apiConfigList && !forceFetch) {
      return this.apiConfigList;
    }
    if (this.fetchConfigPromise) {
      return this.fetchConfigPromise;
    }
    this.fetchConfigPromise = (async () => {
      try {
        const now = Date.now();
        let rawConfigList: any[];
        // commented this code as we are going to fetch config from API only, no local file
        // if (typeof window === 'undefined') {
        //   const { readFileSync } = await import('fs');
        //   const { join } = await import('path');
        //   const configPath = join(process.cwd(), 'public', this.API_CONFIG_PATH);
        //   const json = readFileSync(configPath, 'utf-8');
        //   rawConfigList = JSON.parse(json);
        // } else {
        const apiConfigResponse = this.loadConfigFromStorage();
        if (apiConfigResponse) {
          this.apiConfigList = apiConfigResponse;
          return apiConfigResponse;
        }

        const token = await getToken();
        if (!token) {
          console.warn('[ApiConfigManager] No token found, skipping config fetch — a new token will be generated');
          return [];
        }

        // Guests send no Authorization header; only logged-in users do.
        const anonymous = await isAnonymousSession();
        const res = await fetch(this.API_CONFIG_PATH, {
          headers: {
            ...(!anonymous && { "Authorization": `Bearer ${token}` })
          }
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch cache config: ${res.status}`);
        }
        rawConfigList = await res.json();
        // }
        const normalizedConfigList: IApiConfig[] = [];
        if (!Array.isArray(rawConfigList)) {
          throw new Error('Cache config must be an array');
        }
        for (let i = 0; i < rawConfigList.length; i++) {
          try {
            const normalizedRule = this.normalizeCacheRule(rawConfigList[i]);
            normalizedConfigList.push(normalizedRule);
          } catch (error) {
            console.error(`Invalid cache rule at index ${i}:`, rawConfigList[i], error);
          }
        }
        this.apiConfigList = normalizedConfigList;
        if (typeof window !== 'undefined') {
          this.saveConfigToStorage(normalizedConfigList, now);
        }
        return normalizedConfigList;
      } catch (error) {
        console.error('Error fetching cache rules:', error);
        if (typeof window !== 'undefined') {
          const fallbackConfigList = this.loadConfigFromStorage();
          if (fallbackConfigList) {
            this.apiConfigList = fallbackConfigList;
            return fallbackConfigList;
          }
        }
        return [];
      } finally {
        this.fetchConfigPromise = null;
      }
    })();
    return this.fetchConfigPromise;
  }

  normalizeCacheRule(data: any): IApiConfig {
    if (!data.path || typeof data.path !== 'string') {
      throw new Error('Invalid cache rule path');
    }
    if (!data.method || typeof data.method !== 'string') {
      throw new Error('Invalid cache rule method');
    }
    if (!data.ttl || typeof data.ttl !== 'number' || data.ttl <= 0) {
      throw new Error('Invalid cache rule TTL');
    }
    return {
      path: data.path,
      method: data.method.toUpperCase(),
      enableCache: typeof data.enableCache === 'boolean' ? data.enableCache : true, // default true
      persist: typeof data.persist === 'boolean' ? data.persist : true, // default true
      ttl: typeof data.ttl === 'number' && data.ttl > 0 ? (data.ttl * 1000) : TTL.HOUR, // converting seconds to milliseconds and default will be 1 hour
      showLoading: typeof data.showLoading === 'boolean' ? data.showLoading : true,
      consideredHeaders: Array.isArray(data.consideredHeaders) ? data.consideredHeaders : [],
    };
  }

  saveConfigToStorage(configList: IApiConfig[], timestamp: number): void {
    try {
      const storageObj: IApiConfigStorage = {
        configList,
        timestamp,
        loggedIn: this.isUserLoggedIn,
      };
      localStorage.setItem(this.API_CONFIG_STORAGE_KEY, encodeData(storageObj) || "");
    } catch (error) {
      console.error('Failed to save config to localStorage:', error);
    }
  }

  loadConfigFromStorage(): IApiConfig[] | null {
    try {
      const storedData = localStorage.getItem(this.API_CONFIG_STORAGE_KEY);
      if (!storedData) return null;
      const storageObj = decodeData<IApiConfigStorage>(storedData);
      if (!storageObj) return null;
      const { configList, timestamp } = storageObj;
      const now = Date.now();
      if (!this.isUserLoggedIn) {
        if ((now - timestamp) > this.NON_LOGIN_REFRESH_INTERVAL) {
          this.clearStoredConfig();
          return null;
        }
      }
      return configList;
    } catch (error) {
      console.error('Failed to load config from localStorage:', error);
      this.clearStoredConfig();
      return null;
    }
  }

  clearStoredConfig(): void {
    try {
      localStorage.removeItem(this.API_CONFIG_STORAGE_KEY);
      console.info("[ApiConfigManager] Clear all config.");
    } catch (error) {
      console.error('Failed to clear stored config:', error);
    }
  }

  setUserLoginStatus(loggedIn: boolean): void {
    this.isUserLoggedIn = loggedIn;
    if (!loggedIn) {
      this.apiConfigList = null;
      this.fetchConfigPromise = null;
      this.clearStoredConfig();
    }
  }

  // Reset the config list and clear storage
  resetConfig(): void {
    this.apiConfigList = null;
    this.clearStoredConfig();
  }

  async getApiConfig({ url, method }: { url: string; method: string }): Promise<IApiConfig | null> {
    try {
      this.apiConfigList = await this.fetchApiConfig();
      const normalizedMethod = method.toUpperCase();
      let pathToMatch = extractPathnameFromUrl(url);

      for (const config of this.apiConfigList) {
        if (config.method !== normalizedMethod) continue;
        try {
          if (config.path === pathToMatch) {
            return { ...config };
          }
        } catch (regexError) {
          console.error('Invalid regex pattern in cache rule:', config.path, regexError);
          continue;
        }
      }

      return null;
    } catch (error) {
      console.error('Error in getApiConfig function:', error);
      return null;
    }
  }
}

export const apiConfigManager = new ApiConfigManager();
